// -----------------------------------------------------------
// FloraIQ — Wizard de Correção Assistida
// -----------------------------------------------------------

import { useState } from "react";

interface CorrectionWizardProps {
  isOpen: boolean;
  onClose: () => void;
  taskTitle: string;
  quantity: number;
}

export default function CorrectionWizard({ isOpen, onClose, taskTitle, quantity }: CorrectionWizardProps) {
  const [step, setStep] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [done, setDone] = useState(false);

  if (!isOpen) return null;

  const handleProcess = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      setDone(true);
    }, 2000);
  };

  const steps = [
    { num: 1, label: "Selecionar" },
    { num: 2, label: "Revisar" },
    { num: 3, label: "Aplicar" },
    { num: 4, label: "Confirmar" },
    { num: 5, label: "Resultado" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 fade-in" onClick={onClose}></div>
      <div className="relative bg-white rounded-card w-full max-w-xl mx-4 p-6 slide-up shadow-lg">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold font-heading text-foreground-900">Correção Assistida</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-1 mb-6">
          {steps.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                done ? "bg-green-500 text-white" :
                step >= s.num ? "bg-blue-500 text-white" : "bg-background-200 text-foreground-500"
              }`}>
                {done ? <i className="ri-check-line text-xs"></i> : s.num}
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-0.5 transition-all ${step > s.num ? "bg-blue-500" : "bg-background-200"}`}></div>
              )}
            </div>
          ))}
        </div>

        {/* Conteúdo do step */}
        <div className="min-h-[160px]">
          <p className="text-sm font-medium text-foreground-800 mb-2">{taskTitle}</p>
          <p className="text-xs text-foreground-500 mb-4">{quantity.toLocaleString("pt-BR")} registros afetados</p>

          {!processing && !done && (
            <div className="space-y-2">
              {step <= 2 && <div className="bg-background-50 rounded-lg p-3 border border-background-200/70 text-xs text-foreground-600">Revisando {quantity} registros para aplicar a correção em lote...</div>}
              {step >= 3 && step < 5 && <div className="bg-background-50 rounded-lg p-3 border border-background-200/70 text-xs text-foreground-600">Aplicando correção: {quantity} registros serão atualizados.</div>}
              {step >= 5 && <div className="bg-background-50 rounded-lg p-3 border border-background-200/70 text-xs text-foreground-600">Pronto para confirmar. Esta ação não pode ser desfeita.</div>}
            </div>
          )}

          {processing && (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="w-10 h-10 border-2 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-3"></div>
              <p className="text-sm text-foreground-600">Processando {quantity} registros...</p>
              <p className="text-xs text-foreground-400 mt-1">Isso pode levar alguns segundos</p>
            </div>
          )}

          {done && (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mb-3">
                <i className="ri-check-line text-green-600 text-xl"></i>
              </div>
              <p className="text-sm font-medium text-foreground-800">{quantity} registros corrigidos com sucesso!</p>
              <p className="text-xs text-green-600 mt-1">Qualidade geral: 87% → 89% (+2%)</p>
            </div>
          )}
        </div>

        {/* Botões */}
        <div className="flex gap-3 pt-4 border-t border-background-200/70 mt-4">
          {!done ? (
            <>
              {step > 1 && (
                <button onClick={() => setStep(step - 1)} className="btn-outline text-xs px-3 py-2 cursor-pointer whitespace-nowrap">
                  Voltar
                </button>
              )}
              <button onClick={() => step < 5 ? setStep(step + 1) : handleProcess()} className="btn-primary text-xs px-3 py-2 cursor-pointer whitespace-nowrap ml-auto">
                {step < 5 ? "Próximo" : "Confirmar e Aplicar"}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="btn-primary text-xs px-3 py-2 cursor-pointer whitespace-nowrap w-full">
              Fechar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}